import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Popover,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  Building2,
  X,
  Check,
  MapPin,
  Home,
  TrendingUp,
  Clock,
  Sparkles,
  ChevronDown,
  ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const AMENITIES = [
  'W/D in unit',
  'Dishwasher',
  'Balcony/Patio',
  'Parking included',
  'Pet friendly',
  'Hardwood floors',
  'Stainless appliances',
  'Central AC',
  'Fireplace',
  'Walk-in closet',
  'In-unit laundry',
  'Updated kitchen',
  'Updated bathroom',
];

const AddBuildingWizard = ({ onComplete, onCancel, initialBuilding }) => {
  const { getAccessToken } = useAuth();
  const [step, setStep] = useState('building');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [totalUnits, setTotalUnits] = useState('');
  const [yearBuilt, setYearBuilt] = useState(null);
  const [units, setUnits] = useState([
    {
      id: '1',
      unitNumber: '',
      bedrooms: '',
      bathrooms: '',
      sqft: '',
      currentRent: '',
      amenities: [],
    },
  ]);
  const [amenityAnchor, setAmenityAnchor] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [autocompleteInitialized, setAutocompleteInitialized] = useState(false);
  const initializationTimeoutRef = useRef();

  useEffect(() => {
    if (initialBuilding && initialBuilding.buildingAddress) {
      setBuildingAddress(initialBuilding.buildingAddress);
    }
  }, [initialBuilding]);

  const addUnit = () => {
    setUnits([
      ...units,
      {
        id: Date.now().toString(),
        unitNumber: '',
        bedrooms: '',
        bathrooms: '',
        sqft: '',
        currentRent: '',
        amenities: [],
      },
    ]);
  };

  const removeUnit = id => {
    if (units.length > 1) {
      setUnits(units.filter(u => u.id !== id));
    }
  };

  const updateUnit = (id, field, value) => {
    setUnits(units.map(u => (u.id === id ? { ...u, [field]: value } : u)));
  };

  const toggleAmenity = (unitId, amenity) => {
    setUnits(
      units.map(u => {
        if (u.id === unitId) {
          const amenities = u.amenities.includes(amenity)
            ? u.amenities.filter(a => a !== amenity)
            : [...u.amenities, amenity];
          return { ...u, amenities };
        }
        return u;
      }),
    );
  };

  const handleBuildingNext = () => {
    if (buildingAddress && totalUnits) {
      setStep('units');
    }
  };

  const handleUnitsComplete = () => {
    setStep('market');
  };

  // Initialize Google Places Autocomplete for address field
  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps API is not available, will retry...');
        // Retry initialization after API loads
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          500,
        );
        return;
      }

      if (!addressInputRef.current) {
        console.log('Address input ref not available yet, will retry...');
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          100,
        );
        return;
      }

      if (autocompleteInitialized && autocompleteRef.current) {
        console.log('Autocomplete already initialized.');
        return;
      }

      // Clean up any existing instance
      if (autocompleteRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
          autocompleteRef.current = null;
        } catch (error) {
          console.error('Error cleaning up existing autocomplete:', error);
        }
      }

      console.log('Initializing Google Places Autocomplete for address field.');

      try {
        const options = {
          componentRestrictions: { country: 'us' },
          fields: [
            'address_components',
            'geometry',
            'icon',
            'name',
            'formatted_address',
            'place_id',
          ],
        };

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          options,
        );

        const placeChangedHandler = () => {
          console.log('place_changed event triggered for address field');

          // Add small delay to ensure Google has fully populated the place object
          setTimeout(() => {
            try {
              const place = autocompleteRef.current?.getPlace();
              console.log('Place object received:', place);

              // Enhanced validation for place selection
              if (
                place &&
                place.formatted_address &&
                place.place_id &&
                place.formatted_address.trim() !== ''
              ) {
                const address = place.formatted_address;
                console.log(
                  '✓ Valid autocomplete suggestion selected:',
                  address,
                );
                setBuildingAddress(address);
              } else {
                console.log(
                  '✗ Invalid place selection - missing required data:',
                  {
                    hasPlace: !!place,
                    hasFormattedAddress: !!place?.formatted_address,
                    hasPlaceId: !!place?.place_id,
                    formattedAddress: place?.formatted_address,
                  },
                );
              }
            } catch (error) {
              console.error('Error in place_changed handler:', error);
            }
          }, 10); // Small delay to ensure place object is ready
        };

        autocompleteRef.current.addListener(
          'place_changed',
          placeChangedHandler,
        );

        setAutocompleteInitialized(true);
        console.log(
          '✓ Autocomplete successfully initialized for address field.',
        );
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        setAutocompleteInitialized(false);
        // Retry after a delay
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          1000,
        );
      }
    };

    // Only initialize if we're on the building step
    if (step === 'building') {
      initializeAutocomplete();
    }

    return () => {
      // Clear initialization timeout
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }

      if (autocompleteRef.current) {
        console.log('Cleaning up autocomplete listeners.');
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
        } catch (error) {
          console.error('Error cleaning up autocomplete:', error);
        }
        autocompleteRef.current = null;
      }
      setAutocompleteInitialized(false);
    };
  }, [step]); // Re-initialize when step changes to 'building'

  const handleAddMarket = async () => {
    // Validate units
    const validUnits = units.filter(
      unit =>
        unit.unitNumber &&
        unit.bedrooms !== '' &&
        unit.bathrooms !== '' &&
        unit.sqft &&
        unit.currentRent,
    );

    if (validUnits.length === 0) {
      setSaveError('Please fill in all required fields for at least one unit');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setStep('loading');

    try {
      const accessToken = getAccessToken();

      // Get user ID from localStorage
      let lastModifiedBy = 'user';
      try {
        const mongoUser = JSON.parse(localStorage.getItem('mongodbUser'));
        if (mongoUser && mongoUser._id) {
          lastModifiedBy = mongoUser._id;
        }
      } catch (error) {
        console.warn('Could not get user ID from localStorage:', error);
      }

      // Prepare data for API
      const buildingData = {
        buildingAddress,
        buildingName: buildingName || null,
        totalUnits: parseInt(totalUnits),
        yearBuilt: yearBuilt ? yearBuilt.year() : null,
        units: validUnits.map(unit => ({
          unitNumber: unit.unitNumber,
          bedrooms: unit.bedrooms,
          bathrooms: unit.bathrooms,
          sqft: unit.sqft,
          currentRent: unit.currentRent,
          status: 'occupied',
          amenities: unit.amenities || [],
        })),
        lastModifiedBy,
      };

      const url = `${process.env.REACT_APP_NODE_URL}/buildings/buildings`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(buildingData),
      });

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to save building');
      }

      // Success - show loading screen briefly then complete
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      console.error('Error saving building:', error);
      setSaveError(
        error.message || 'Failed to save building. Please try again.',
      );
      setSaving(false);
      setStep('market'); // Go back to market step to show error
    }
  };

  if (step === 'building') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 100px)',
          padding: '2rem',
        }}
      >
        <Card
          sx={{
            maxWidth: '800px',
            width: '100%',
            padding: '2rem',
            borderRadius: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem',
            }}
          >
            <IconButton
              onClick={onCancel}
              sx={{
                padding: '0.5rem',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <ArrowLeft size={20} color='#39585C' />
            </IconButton>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#E8F0F1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Building2 size={20} color='#39585C' />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Add Your Building
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: '#6B7280',
                }}
              >
                Start by entering your building details
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Building Address *
              </Typography>
              <TextField
                inputRef={addressInputRef}
                fullWidth
                placeholder='123 Main St, Dallas, TX 75201'
                value={buildingAddress}
                onChange={e => {
                  // Only set input without autocomplete flag for manual typing
                  setBuildingAddress(e.target.value);
                }}
                onFocus={() => {
                  console.log('Address input field focused');
                  // Re-initialize autocomplete if it failed previously
                  if (
                    !autocompleteInitialized &&
                    window.google &&
                    window.google.maps &&
                    window.google.maps.places
                  ) {
                    console.log(
                      'Retrying autocomplete initialization on focus...',
                    );
                    setAutocompleteInitialized(false);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <MapPin
                      size={16}
                      style={{ marginRight: '0.5rem', color: '#6B7280' }}
                    />
                  ),
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  marginTop: '0.25rem',
                }}
              >
                We'll use this to find comparable properties nearby
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Building Name (Optional)
              </Typography>
              <TextField
                fullWidth
                placeholder='e.g., Sunset Apartments'
                value={buildingName}
                onChange={e => setBuildingName(e.target.value)}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Total Units *
              </Typography>
              <TextField
                fullWidth
                type='number'
                placeholder='e.g., 24'
                value={totalUnits}
                onChange={e => setTotalUnits(e.target.value)}
                InputProps={{
                  inputProps: { min: 1 },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Year Built (Optional)
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year']}
                  value={yearBuilt}
                  onChange={newValue => setYearBuilt(newValue)}
                  minDate={dayjs('1800')}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'e.g., 2015',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem' }}>
            <Button
              variant='outlined'
              onClick={onCancel}
              fullWidth
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleBuildingNext}
              disabled={!buildingAddress || !totalUnits}
              fullWidth
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
                '&:disabled': { backgroundColor: '#9CA3AF' },
              }}
            >
              Next: Add Units
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  if (step === 'units') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 100px)',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '1rem',
          }}
        >
          <Box
            sx={{
              maxWidth: '1280px',
              marginX: 'auto',
              display: 'flex',
              alignItems: { xs: 'start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '0.75rem', sm: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <IconButton
                onClick={onCancel}
                sx={{
                  padding: '0.5rem',
                  '&:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                }}
              >
                <ArrowLeft size={20} color='#39585C' />
              </IconButton>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Add Units
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    color: '#6B7280',
                    marginTop: '0.25rem',
                  }}
                >
                  {buildingName || buildingAddress} • {units.length} unit
                  {units.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
            <Button
              variant='text'
              onClick={() => setStep('building')}
              sx={{
                textTransform: 'none',
                color: '#39585C',
              }}
            >
              Edit Building
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: { xs: '1rem', md: '1.5rem' },
          }}
        >
          <Box
            sx={{
              maxWidth: '1280px',
              marginX: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            {units.map((unit, index) => (
              <Card key={unit.id} sx={{ padding: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <Box
                    sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#E8F0F1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '1.5rem',
                    }}
                  >
                    <Home size={16} color='#39585C' />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(3, 1fr)',
                          lg: 'repeat(6, 1fr)',
                        },
                        gap: '0.75rem',
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Unit # *
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          placeholder='101'
                          value={unit.unitNumber}
                          onChange={e =>
                            updateUnit(unit.id, 'unitNumber', e.target.value)
                          }
                        />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Bedrooms *
                        </Typography>
                        <FormControl fullWidth size='small'>
                          <Select
                            value={unit.bedrooms}
                            onChange={e =>
                              updateUnit(unit.id, 'bedrooms', e.target.value)
                            }
                          >
                            <MenuItem value=''>Select</MenuItem>
                            <MenuItem value='0'>Studio</MenuItem>
                            <MenuItem value='1'>1 BR</MenuItem>
                            <MenuItem value='2'>2 BR</MenuItem>
                            <MenuItem value='3'>3 BR</MenuItem>
                            <MenuItem value='4'>4 BR</MenuItem>
                            <MenuItem value='5'>5 BR</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Bathrooms *
                        </Typography>
                        <FormControl fullWidth size='small'>
                          <Select
                            value={unit.bathrooms}
                            onChange={e =>
                              updateUnit(unit.id, 'bathrooms', e.target.value)
                            }
                          >
                            <MenuItem value=''>Select</MenuItem>
                            <MenuItem value='1'>1</MenuItem>
                            <MenuItem value='1.5'>1.5</MenuItem>
                            <MenuItem value='2'>2</MenuItem>
                            <MenuItem value='2.5'>2.5</MenuItem>
                            <MenuItem value='3'>3</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Sq Ft *
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          type='number'
                          placeholder='850'
                          value={unit.sqft}
                          onChange={e =>
                            updateUnit(unit.id, 'sqft', e.target.value)
                          }
                        />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Current Rent *
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          type='number'
                          placeholder='1200'
                          value={unit.currentRent}
                          onChange={e =>
                            updateUnit(unit.id, 'currentRent', e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <Typography
                                sx={{
                                  marginRight: '0.5rem',
                                  color: '#6B7280',
                                }}
                              >
                                $
                              </Typography>
                            ),
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.5rem',
                            color: '#374151',
                          }}
                        >
                          Amenities
                        </Typography>
                        <Button
                          variant='outlined'
                          fullWidth
                          size='small'
                          onClick={e =>
                            setAmenityAnchor({
                              ...amenityAnchor,
                              [unit.id]: e.currentTarget,
                            })
                          }
                          sx={{
                            textTransform: 'none',
                            justifyContent: 'space-between',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            '&:hover': {
                              borderColor: '#9CA3AF',
                              backgroundColor: '#F9FAFB',
                            },
                          }}
                        >
                          {unit.amenities.length > 0
                            ? `${unit.amenities.length} selected`
                            : 'Select'}
                          <ChevronDown size={16} />
                        </Button>
                        <Popover
                          open={Boolean(amenityAnchor[unit.id])}
                          anchorEl={amenityAnchor[unit.id]}
                          onClose={() =>
                            setAmenityAnchor({
                              ...amenityAnchor,
                              [unit.id]: null,
                            })
                          }
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                        >
                          <Box
                            sx={{
                              padding: '1rem',
                              width: { xs: '280px', sm: '400px' },
                              maxHeight: '300px',
                              overflowY: 'auto',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                marginBottom: '0.75rem',
                                color: '#111827',
                              }}
                            >
                              Select amenities
                            </Typography>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  sm: 'repeat(2, 1fr)',
                                },
                                gap: '0.75rem',
                              }}
                            >
                              {AMENITIES.map(amenity => (
                                <FormControlLabel
                                  key={amenity}
                                  control={
                                    <Checkbox
                                      checked={unit.amenities.includes(amenity)}
                                      onChange={() =>
                                        toggleAmenity(unit.id, amenity)
                                      }
                                      size='small'
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{
                                        fontSize: '0.875rem',
                                        color: '#111827',
                                      }}
                                    >
                                      {amenity}
                                    </Typography>
                                  }
                                />
                              ))}
                            </Box>
                          </Box>
                        </Popover>
                      </Box>
                    </Box>

                    {unit.amenities.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.25rem',
                          marginTop: '0.5rem',
                        }}
                      >
                        {unit.amenities.map(amenity => (
                          <Chip
                            key={amenity}
                            label={amenity}
                            size='small'
                            onDelete={() => toggleAmenity(unit.id, amenity)}
                            sx={{
                              backgroundColor: '#E8F0F1',
                              color: '#39585C',
                              '& .MuiChip-deleteIcon': {
                                color: '#39585C',
                                '&:hover': { color: '#2A3F42' },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {units.length > 1 && (
                    <IconButton
                      onClick={() => removeUnit(unit.id)}
                      sx={{
                        flexShrink: 0,
                        marginTop: '1.5rem',
                        '&:hover': { backgroundColor: '#FEE2E2' },
                      }}
                    >
                      <X size={16} color='#6B7280' />
                    </IconButton>
                  )}
                </Box>
              </Card>
            ))}
            <Button
              variant='outlined'
              onClick={addUnit}
              fullWidth
              startIcon={<Home size={16} />}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Add Another Unit
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '1rem',
          }}
        >
          <Box
            sx={{
              maxWidth: '1280px',
              marginX: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: { xs: '0.75rem', sm: 0 },
            }}
          >
            <Button
              variant='text'
              onClick={onCancel}
              sx={{
                textTransform: 'none',
                color: '#6B7280',
              }}
            >
              Cancel
            </Button>
            <Box
              sx={{
                display: 'flex',
                gap: '0.75rem',
                width: { xs: '100%', sm: 'auto' },
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant='outlined'
                onClick={() => setStep('building')}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                  display: { xs: 'block', sm: 'inline-flex' },
                }}
              >
                Back
              </Button>
              <Button
                variant='contained'
                onClick={handleUnitsComplete}
                startIcon={<Check size={16} />}
                fullWidth
                sx={{
                  backgroundColor: '#39585C',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#2A3F42' },
                  display: { xs: 'block', sm: 'inline-flex' },
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (step === 'market') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 'calc(100vh - 80px)', md: 'calc(100vh - 100px)' },
          padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
        }}
      >
        <Card
          sx={{
            maxWidth: '800px',
            width: '100%',
            padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            borderRadius: { xs: '0.5rem', md: '0.75rem' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem',
            }}
          >
            <IconButton
              onClick={onCancel}
              sx={{
                padding: '0.5rem',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <ArrowLeft size={20} color='#39585C' />
            </IconButton>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#E8F0F1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TrendingUp size={20} color='#4FA490' />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Add Building
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: '#6B7280',
                }}
              >
                Generate pricing comparables and recommendations
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
            }}
          >
            <Box
              sx={{
                padding: '1rem',
                borderRadius: '0.5rem',
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}
              >
                <Sparkles
                  size={20}
                  color='#4FA490'
                  style={{ flexShrink: 0, marginTop: '0.125rem' }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    What we'll analyze:
                  </Typography>
                  <Box
                    component='ul'
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      margin: 0,
                      paddingLeft: '1.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                    }}
                  >
                    <li>Find comparable properties nearby</li>
                    <li>Analyze current market rents by bedroom type</li>
                    <li>Calculate pricing recommendations for each unit</li>
                    <li>Identify opportunities for rent optimization</li>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Alert
              severity='warning'
              icon={<Clock size={16} />}
              sx={{
                backgroundColor: '#FEF3C7',
                borderColor: '#FDE68A',
                '& .MuiAlert-icon': {
                  color: '#D97706',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#111827',
                }}
              >
                Market analysis takes up to <strong>48 hours</strong> to
                complete. We'll notify you when your data is ready.
              </Typography>
            </Alert>

            <Box
              sx={{
                padding: '1rem',
                borderRadius: '0.5rem',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  Building Summary
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                <Typography>
                  <strong style={{ color: '#111827' }}>
                    {buildingName || 'Building'}:
                  </strong>{' '}
                  {buildingAddress}
                </Typography>
                <Typography>
                  <strong style={{ color: '#111827' }}>Total Units:</strong>{' '}
                  {totalUnits}
                </Typography>
                <Typography>
                  <strong style={{ color: '#111827' }}>Units Added:</strong>{' '}
                  {units.length} unit{units.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          </Box>

          {saveError && (
            <Alert
              severity='error'
              sx={{ marginBottom: '1rem' }}
              onClose={() => setSaveError(null)}
            >
              {saveError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem' }}>
            <Button
              variant='outlined'
              onClick={onCancel}
              fullWidth
              disabled={saving}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Cancel
            </Button>
            <Button
              variant='outlined'
              onClick={() => setStep('units')}
              fullWidth
              disabled={saving}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Back to Units
            </Button>
            <Button
              variant='contained'
              onClick={handleAddMarket}
              fullWidth
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={16} />
                ) : (
                  <TrendingUp size={16} />
                )
              }
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
                '&:disabled': { backgroundColor: '#9CA3AF' },
              }}
            >
              {saving ? 'Saving...' : 'Add Building'}
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  if (step === 'loading') {
    const hasValidUnits = units.some(
      unit =>
        unit.unitNumber &&
        unit.bedrooms &&
        unit.bathrooms &&
        unit.sqft &&
        unit.currentRent,
    );

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 'calc(100vh - 80px)', md: 'calc(100vh - 100px)' },
          padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
        }}
      >
        <Card
          sx={{
            maxWidth: '800px',
            width: '100%',
            padding: { xs: '1.5rem', sm: '2rem', md: '3rem' },
            borderRadius: { xs: '0.5rem', md: '0.75rem' },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.5rem',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  width: { xs: '64px', md: '96px' },
                  height: { xs: '64px', md: '96px' },
                  borderRadius: '50%',
                  backgroundColor: '#E8F0F1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
              >
                <TrendingUp size={32} color='#4FA490' />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#4FA490',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sparkles size={16} color='white' />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.875rem' },
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Analyzing Market Data
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', md: '1.125rem' },
                color: '#6B7280',
                paddingX: { xs: '0.5rem', md: 0 },
              }}
            >
              We're finding comparable properties and generating pricing
              recommendations
            </Typography>
          </Box>

          <Box
            sx={{
              maxWidth: '28rem',
              marginX: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
            }}
          >
            <Box
              sx={{
                padding: '1.5rem',
                borderRadius: '0.5rem',
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  Estimated Time Remaining
                </Typography>
                <Clock size={16} color='#6B7280' />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.5rem', md: '2.25rem' },
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '0.5rem',
                  fontFamily: 'monospace',
                }}
              >
                47:32:15
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Hours : Minutes : Seconds
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                textAlign: 'left',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}
              >
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#4FA490',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '0.125rem',
                  }}
                >
                  <Check size={12} color='white' />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Building Added
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    {units.length} units saved
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}
              >
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#F9FAFB',
                    border: '2px solid #4FA490',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '0.125rem',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  }}
                >
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#4FA490',
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Finding Comparables
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    Searching nearby properties
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: '0.75rem',
                  opacity: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#F9FAFB',
                    border: '2px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '0.125rem',
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Calculating Recommendations
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    Analyzing market trends
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: '0.75rem',
                  opacity: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#F9FAFB',
                    border: '2px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '0.125rem',
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Generating Reports
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    Creating insights and opportunities
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              paddingTop: '1rem',
            }}
          >
            <Button
              onClick={() => {
                setBuildingAddress('');
                setBuildingName('');
                setTotalUnits('');
                setYearBuilt(null);
                setUnits([
                  {
                    id: '1',
                    unitNumber: '',
                    bedrooms: '',
                    bathrooms: '',
                    sqft: '',
                    currentRent: '',
                    amenities: [],
                  },
                ]);
                setStep('building');
              }}
              variant='contained'
              fullWidth
              startIcon={<Building2 size={16} />}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
              }}
            >
              Add Building
            </Button>

            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Button
                onClick={onCancel}
                variant='outlined'
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onComplete}
                variant='outlined'
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                {hasValidUnits ? 'Continue to Portfolio' : 'Close'}
              </Button>
            </Box>

            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
                marginTop: '0.5rem',
                textAlign: 'center',
              }}
            >
              {hasValidUnits
                ? "You can add more buildings or return to view your scraper status. We'll email you when the analysis is complete."
                : "We'll email you when the analysis is complete. Return to view your scraper status."}
            </Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  return null;
};

export default AddBuildingWizard;
